import { FC, useRef, useState } from 'react';
import { Menu } from 'primereact/menu';
import { MenuItem, MenuItemOptions } from 'primereact/menuitem';
import { Tooltip } from 'primereact/tooltip';
import { CalendarClock, ChevronDown, ChevronUp, CircleMinus, LucideIcon } from 'lucide-react';

interface Props {
  disabled?: boolean;
  onReschedule: () => void;
  onCancel: () => void;
}

const itemRowClass =
  'tw-flex tw-h-11 tw-w-full tw-cursor-pointer tw-items-center tw-gap-2.5 tw-rounded-[10px] tw-border-none tw-bg-white tw-px-3 tw-text-left tw-font-atkinson tw-text-[15px] tw-font-bold tw-leading-5 tw-transition-colors tw-duration-[140ms] tw-ease-out hover:tw-bg-[#F9F8F5]';

//PrimeReact positions the popup below its target and only flips when it would overflow the
//viewport; the design anchors it above the trigger, so the inline top/left it writes are
//overridden here rather than left to that fallback.
const panelClass =
  '!tw-absolute !tw-bottom-[calc(100%_+_8px)] !tw-left-auto !tw-right-0 !tw-top-auto !tw-w-[236px] !tw-origin-bottom-right !tw-rounded-2xl !tw-border-2 !tw-border-b-4 !tw-border-solid !tw-border-neutral-300 !tw-bg-white !tw-p-2 !tw-shadow-[0_0_15px_0_rgba(132,123,111,0.15)] tw-animate-menuReveal';

//_extensions.scss hands anything carrying .p-focus, :focus or :focus-visible the theme's focus
//mixin, whose ring is a `box-shadow: 0 0 0 6px #0a41bd` rather than the outline the name
//suggests — that one is `0 none`. PrimeReact focuses the list when the popup opens and marks the
//active row .p-focus, so the ring boxed the panel and banded the gap between the rows. Both
//properties go, and the row's hover background stands in so keyboard focus is still visible.
//Two things the scanner forces here: the selector is spelled out in every entry, because
//Tailwind only sees literal class strings and an interpolated one produces no CSS at all; and
//the three states ride in one :is(), because a bare comma in an arbitrary variant compiles to
//nothing, silently.
const focusClass = [
  '[&_:is(:focus,:focus-visible,.p-focus)]:!tw-shadow-none',
  '[&_:is(:focus,:focus-visible,.p-focus)]:!tw-outline-none',
  '[&_.p-menuitem.p-focus_button]:tw-bg-[#F9F8F5]',
].join(' ');

//named rather than an arrow because react/display-name reads this as a component: PrimeReact
//calls a MenuItem template as template(item, options) and never renders it as an element, so
//the name is only here to satisfy the rule
const row = (Icon: LucideIcon, colorClass: string) =>
  function MenuRow(item: MenuItem, options: MenuItemOptions) {
    return (
      <button type="button" tabIndex={-1} onClick={options.onClick} className={`${itemRowClass} ${colorClass}`}>
        <Icon size={20} className="tw-shrink-0" aria-hidden />
        <span className="tw-min-w-0 tw-grow tw-whitespace-nowrap">{item.label}</span>
      </button>
    );
  };

const ExitSlipSnapshotEditAssignmentMenu: FC<Props> = ({ disabled = false, onReschedule, onCancel }) => {
  const menu = useRef<Menu>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  //PrimeReact's Menu owns the ARIA menu semantics, keyboard navigation, outside-click and Escape;
  //we just supply the model and the row markup.
  const items: MenuItem[] = [
    { label: 'Reschedule', command: onReschedule, template: row(CalendarClock, 'tw-text-neutral-950') },
    { label: 'Cancel', command: onCancel, template: row(CircleMinus, 'tw-text-[#E33434]') },
  ];

  return (
    <div className="tw-relative">
      <button
        ref={trigger}
        type="button"
        aria-haspopup
        aria-expanded={open}
        aria-disabled={disabled}
        onClick={event => !disabled && menu.current?.toggle(event)}
        className={`tw-flex tw-h-11 tw-items-center tw-gap-2 tw-rounded-full tw-border-none tw-px-4 tw-font-atkinson tw-text-sm tw-font-bold tw-leading-5 tw-transition-colors tw-duration-[140ms] tw-ease-out ${
          disabled
            ? 'tw-cursor-not-allowed tw-bg-transparent tw-text-neutral-600'
            : open
            ? 'tw-cursor-pointer tw-bg-indigo-700 tw-text-white'
            : 'tw-cursor-pointer tw-bg-transparent tw-text-indigo-600 hover:tw-bg-black/[0.06]'
        }`}
      >
        Manage today&apos;s ticket
        {open ? <ChevronUp size={16} strokeWidth={2.5} /> : <ChevronDown size={16} strokeWidth={2.5} />}
      </button>

      {disabled && (
        <Tooltip target={trigger} event="both" position="bottom" className="nc_demo-disabled-tooltip tw-w-[263px]">
          Exit tickets can only be managed for one class at a time.
        </Tooltip>
      )}

      <Menu
        ref={menu}
        popup
        model={items}
        appendTo="self"
        onShow={() => setOpen(true)}
        onHide={() => setOpen(false)}
        pt={{
          root: { className: `${panelClass} ${focusClass} !tw-outline-none` },
          menu: { className: '!tw-m-0 !tw-flex !tw-list-none !tw-flex-col !tw-gap-1.5 !tw-p-0 !tw-outline-none' },
        }}
      />
    </div>
  );
};

export default ExitSlipSnapshotEditAssignmentMenu;
